(() => {
  const cfg = window.EMESA_THEME || {};
  const routes = cfg.routes || {};
  const cartUrl = routes.cart_url || "/cart";
  const addUrl = routes.cart_add_url || "/cart/add.js";
  const changeUrl = routes.cart_change_url || "/cart/change.js";
  const predictUrl = routes.predictive_search_url || "/search/suggest";
  const WISH_KEY = "emesa_wishlist";

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const formatMoney = (cents) => {
    if (cents == null) return "";
    const numeric = Number(cents) / 100;
    const value = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numeric);
    return `${value} ${cfg.currencyIso || "AED"}`;
  };

  const toast = (message) => {
    const el = $("[data-toast]");
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add("is-visible");
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => el.classList.remove("is-visible"), 2400);
  };

  const lock = (on) => document.body.classList.toggle("is-locked", on);

  const closeAll = () => {
    $$("[data-drawer]").forEach((el) => {
      el.classList.remove("is-open");
      el.setAttribute("aria-hidden", "true");
      el.removeAttribute("aria-modal");
    });
    $("[data-overlay]")?.classList.remove("is-open");
    lock(false);
  };

  const openDrawer = (name) => {
    const target = $(`[data-drawer="${name}"]`);
    if (!target) return;
    closeAll();
    target.classList.add("is-open");
    target.setAttribute("aria-hidden", "false");
    target.setAttribute("aria-modal", "true");
    $("[data-overlay]")?.classList.add("is-open");
    lock(true);
    const focusable = target.querySelector("input, button, a, textarea, select");
    focusable?.focus();
  };

  const wishRead = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(WISH_KEY) || "[]");
      if (!Array.isArray(stored)) return [];
      return stored.filter((handle) => typeof handle === "string" && /^[a-z0-9][a-z0-9-]*$/i.test(handle)).slice(0, 100);
    } catch {
      return [];
    }
  };
  const wishWrite = (list) => localStorage.setItem(WISH_KEY, JSON.stringify(list));
  const wishHas = (handle) => wishRead().includes(handle);
  const wishToggle = (handle) => {
    const list = wishRead();
    const i = list.indexOf(handle);
    if (i >= 0) list.splice(i, 1);
    else list.push(handle);
    wishWrite(list);
    return wishHas(handle);
  };

  const syncWish = () => {
    $$("[data-wish]").forEach((btn) => {
      const on = wishHas(btn.dataset.wish);
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-pressed", String(on));
    });
  };

  const updateCartDot = (count) => {
    $$("[data-cart-dot]").forEach((dot) => {
      const n = Number(count || 0);
      dot.hidden = n === 0;
      dot.textContent = n > 9 ? "9+" : String(n);
      dot.classList.toggle("is-on", n > 0);
    });
  };

  const fetchCart = () => fetch(`${cartUrl}.js`, { credentials: "same-origin" }).then((r) => r.json());

  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;"
  })[char]);

  const safeUrl = (value, fallback = "#") => {
    const raw = String(value || "").trim();
    if (!raw) return fallback;
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return fallback;
      return raw;
    } catch {
      return fallback;
    }
  };

  const safeUploadedFileUrl = (value, fallback = "") => {
    const raw = String(value || "").trim();
    if (!raw) return fallback;
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && parsed.origin === window.location.origin)) return fallback;
      const sameStoreUpload = parsed.origin === window.location.origin && parsed.pathname.startsWith("/uploads/");
      const shopifyCdnUpload = parsed.protocol === "https:"
        && parsed.hostname === "cdn.shopify.com"
        && (parsed.pathname.startsWith("/s/files/") || parsed.pathname.startsWith("/cdn/"));
      return sameStoreUpload || shopifyCdnUpload ? parsed.href : fallback;
    } catch {
      return fallback;
    }
  };

  const linePropertyUrl = (item, ...keys) => {
    for (const key of keys) {
      const value = item?.properties?.[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return "";
  };

  const originalPhotoUrl = (item) => linePropertyUrl(item, "_Custom Photo", "Custom Photo");
  const artworkPreviewUrl = (item) => linePropertyUrl(item, "_Artwork Preview", "Artwork Preview");
  const uploadedPhotoUrl = (item) => artworkPreviewUrl(item) || originalPhotoUrl(item);

  const cartImageMarkup = (item) => {
    const customPhoto = uploadedPhotoUrl(item);
    const customSource = safeUploadedFileUrl(customPhoto);
    const source = customSource || safeUrl(item.image || "", "");
    if (!source) return '<span class="cart-thumb-placeholder" aria-hidden="true"><svg class="icon"><use href="#icon-image"></use></svg></span>';
    return `<img${customSource ? ' class="cart-custom-photo"' : ""} src="${escapeHtml(source)}" alt="${escapeHtml(customSource ? (cfg.strings?.uploadedArtwork || "Your uploaded artwork") : item.product_title || cfg.strings?.artwork || "Artwork")}" width="88" height="110" loading="lazy">`;
  };

  const propertyLines = (item) =>
    Object.entries(item.properties || {})
      .filter(([k, v]) => v && !String(k).startsWith("_") && !["Custom Photo", "Artwork Preview"].includes(k))
      .map(([k, v]) => {
        const fileUrl = safeUploadedFileUrl(v);
        const label = k === "Personal Message"
          ? (cfg.strings?.propertyMessage || k)
          : k === "Women-Only Photo Handling" ? (cfg.strings?.propertyWomenOnly || k) : k;
        const displayValue = k === "Women-Only Photo Handling" && v === "Requested" ? (cfg.strings?.requested || v) : v;
        return `<p class="cart-note">${escapeHtml(label)}: ${fileUrl ? `<a href="${escapeHtml(fileUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(cfg.strings?.uploadedFile || "Uploaded file")}</a>` : escapeHtml(displayValue)}</p>`;
      })
      .join("");

  const renderCartDrawer = (cart) => {
    const root = $("[data-cart-drawer]");
    if (!root || !cart) return;
    updateCartDot(cart.item_count);
    if (!cart.items.length) {
      root.innerHTML = `
        <div class="empty-state">
          <svg class="icon icon-lg icon-gold" aria-hidden="true"><use href="#icon-bag"></use></svg>
          <h3>${escapeHtml(cfg.strings?.cartEmpty || "Your bag is empty")}</h3>
          <p>${escapeHtml(cfg.strings?.cartEmptyCopy || "Your bag is waiting for its first piece of metal art.")}</p>
          <a class="btn btn-primary" href="${escapeHtml(safeUrl(routes.all_products_collection_url || "/collections/metal-wall-art"))}" data-close>${escapeHtml(cfg.strings?.cartExplore || "Explore Metal Art")}</a>
        </div>`;
      return;
    }
    root.innerHTML = `
      <div class="cart-lines">
        ${cart.items
          .map(
            (item) => `
          <article class="cart-line" data-key="${escapeHtml(item.key)}" data-line="${escapeHtml(item.index || "")}">
            <a class="cart-thumb" href="${escapeHtml(safeUrl(item.url))}">
              ${cartImageMarkup(item)}
            </a>
            <div class="cart-line-body">
              <h3>${escapeHtml(item.product_title)}</h3>
              <p>${item.variant_title && item.variant_title !== "Default Title" ? escapeHtml(item.variant_title) : ""}</p>
              ${propertyLines(item)}
              <div class="cart-line-row">
                <div class="qty" role="group" aria-label="${escapeHtml(cfg.strings?.cartQuantity || "Quantity")}">
                  <button type="button" data-qty="-1" aria-label="${escapeHtml(cfg.strings?.decreaseQuantity || "Decrease quantity")}"><svg class="icon icon-sm" aria-hidden="true"><use href="#icon-minus"></use></svg></button>
                  <span>${item.quantity}</span>
                  <button type="button" data-qty="1" aria-label="${escapeHtml(cfg.strings?.increaseQuantity || "Increase quantity")}"><svg class="icon icon-sm" aria-hidden="true"><use href="#icon-plus"></use></svg></button>
                </div>
                <p class="price">${formatMoney(item.final_line_price)}</p>
                <button class="icon-btn" type="button" data-remove aria-label="${escapeHtml(cfg.strings?.cartRemove || "Remove")} ${escapeHtml(item.product_title)}">
                  <svg class="icon" aria-hidden="true"><use href="#icon-trash"></use></svg>
                </button>
              </div>
            </div>
          </article>`
          )
          .join("")}
      </div>
      <div class="cart-drawer-foot">
        <div class="cart-subtotal"><span>${escapeHtml(cfg.strings?.cartSubtotal || "Subtotal")}</span><strong>${formatMoney(cart.total_price)}</strong></div>
        <p class="muted-note">${escapeHtml(cfg.strings?.cartShipping || "Shipping calculated at checkout")}</p>
        <a class="btn btn-primary btn-block" href="${escapeHtml(safeUrl(cartUrl))}">${escapeHtml(cfg.strings?.cartView || "View Bag")}</a>
        <form action="${escapeHtml(safeUrl(cartUrl))}" method="post">
          <button class="btn btn-secondary btn-block" type="submit" name="checkout">${escapeHtml(cfg.strings?.cartCheckout || "Proceed to Checkout")}</button>
        </form>
        <button class="text-link" type="button" data-close>${escapeHtml(cfg.strings?.cartContinue || "Continue shopping")}</button>
      </div>`;
  };

  const renderCartUploadLoading = (form) => {
    const root = $("[data-cart-drawer]");
    if (!root) return;
    const preview = form.closest("[data-customizer]")?.querySelector("[data-mobile-preview-image]");
    const previewMarkup = preview?.src
      ? `<img src="${escapeHtml(preview.src)}" alt="${escapeHtml(cfg.strings?.artworkPreview || "Your artwork preview")}">`
      : '<svg class="icon icon-lg icon-gold" aria-hidden="true"><use href="#icon-image"></use></svg>';
    root.innerHTML = `
      <div class="cart-uploading" role="status" aria-live="polite">
        <div class="cart-uploading__preview">${previewMarkup}</div>
        <span class="cart-uploading__spinner" aria-hidden="true"></span>
        <h3>${escapeHtml(cfg.strings?.uploadingTitle || "Adding your artwork…")}</h3>
        <p>${escapeHtml(cfg.strings?.uploadingBody || "Please keep this window open while your print-ready files upload securely.")}</p>
      </div>`;
  };

  const refreshCart = () => fetchCart().then(renderCartDrawer).catch(() => {});

  const changeLine = (key, quantity) =>
    fetch(changeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());

  const addToCart = async (payload) => {
    const isForm = payload instanceof FormData;
    const res = await fetch(addUrl, {
      method: "POST",
      headers: isForm ? { Accept: "application/json" } : { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: isForm ? payload : JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    return data;
  };

  const parseProductJson = (root) => {
    const node = root.querySelector("[data-product-json]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  };

  const optionValues = (form) =>
    [...form.querySelectorAll("[data-option-input]:checked")].map((el) => el.value);

  const findVariant = (product, values) => {
    if (!product?.variants) return null;
    return product.variants.find((v) => values.every((val, i) => String(v.options[i]) === String(val))) || product.variants[0];
  };

  const addBusinessDays = (start, amount) => {
    const date = new Date(start);
    let remaining = Math.max(0, Number(amount) || 0);
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) remaining -= 1;
    }
    return date;
  };

  const bindDeliveryEstimate = (section) => {
    const estimate = section?.querySelector("[data-delivery-estimate]");
    if (!estimate || estimate.dataset.bound === "delivery-estimate") return;
    estimate.dataset.bound = "delivery-estimate";
    const output = estimate.querySelector("[data-delivery-range]");
    if (!output) return;
    const today = new Date();
    const minimum = addBusinessDays(today, estimate.dataset.minBusinessDays);
    const maximum = addBusinessDays(today, estimate.dataset.maxBusinessDays);
    const locale = cfg.locale || document.documentElement.lang || "en";
    const sameYear = minimum.getFullYear() === maximum.getFullYear();
    const sameMonth = minimum.getMonth() === maximum.getMonth() && sameYear;
    const rtl = document.documentElement.dir === "rtl" || String(locale).toLowerCase().startsWith("ar");
    const shortDate = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
    const monthOnly = new Intl.DateTimeFormat(locale, { month: "short" });
    const dayOnly = new Intl.DateTimeFormat(locale, { day: "numeric" });
    output.textContent = sameMonth
      ? (rtl
        ? `${dayOnly.format(minimum)}–${dayOnly.format(maximum)} ${monthOnly.format(maximum)}`
        : `${monthOnly.format(minimum)} ${dayOnly.format(minimum)}–${dayOnly.format(maximum)}`)
      : `${shortDate.format(minimum)} – ${shortDate.format(maximum)}`;
  };

  const bindStickyPurchase = (section) => {
    const sticky = $("[data-sticky-purchase]");
    const mainAdd = section?.querySelector("[data-add-to-cart]");
    const stickyAdd = sticky?.querySelector("[data-sticky-add]");
    if (!sticky || !mainAdd || !stickyAdd || sticky.dataset.bound === "sticky-purchase") return;
    sticky.dataset.bound = "sticky-purchase";
    stickyAdd.addEventListener("click", () => {
      if (!mainAdd.disabled) mainAdd.click();
    });
    const updateVisibility = () => {
      const mobile = window.matchMedia("(max-width: 749px)").matches;
      const buttonRect = mainAdd.getBoundingClientRect();
      const visible = mobile && buttonRect.bottom < 72;
      sticky.classList.toggle("is-visible", visible);
      sticky.setAttribute("aria-hidden", String(!visible));
    };
    updateVisibility();
    window.addEventListener("scroll", updateVisibility, { passive: true });
    window.addEventListener("resize", updateVisibility, { passive: true });
  };

  const updateProductUI = (section, variant) => {
    if (!variant) return;
    const idInput = section.querySelector("[data-variant-id]");
    if (idInput) idInput.value = variant.id;
    section.querySelectorAll("[data-live-price]").forEach((el) => {
      el.textContent = formatMoney(variant.price);
    });
    const priceWrap = section.querySelector("[data-price-wrap]");
    if (priceWrap) {
      priceWrap.innerHTML = `<div class="price${variant.compare_at_price > variant.price ? " price--on-sale" : ""}">${
        variant.compare_at_price > variant.price ? `<s class="price--compare">${formatMoney(variant.compare_at_price)}</s>` : ""
      }<span class="price__regular">${formatMoney(variant.price)}</span></div>`;
    }
    const add = section.querySelector("[data-add-to-cart]");
    if (add) {
      add.disabled = !variant.available;
      const text = add.querySelector("[data-add-text]");
      if (text) text.textContent = variant.available ? (cfg.strings?.productAdd || "Add to Cart") : (cfg.strings?.productSoldOut || "Sold out");
    }
    const stickyAdd = $("[data-sticky-add]");
    if (stickyAdd) {
      stickyAdd.disabled = !variant.available;
      const stickyText = stickyAdd.querySelector("[data-sticky-add-text]");
      if (stickyText) stickyText.textContent = variant.available ? (cfg.strings?.productAdd || "Add to Cart") : (cfg.strings?.productSoldOut || "Sold out");
    }
    const stickyPrice = $("[data-sticky-purchase] [data-live-price]");
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    if (variant.featured_image) {
      const media = section.querySelector(`[data-media-id="${variant.featured_image.id}"]`);
      if (media) showMedia(section, variant.featured_image.id);
    }
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    history.replaceState({}, "", url);
  };

  const showMedia = (root, id) => {
    const activate = () => {
      root.querySelectorAll("[data-media-id]").forEach((el) => {
        const on = String(el.dataset.mediaId) === String(id);
        el.classList.toggle("is-active", on);
        el.hidden = !on;
      });
      root.querySelectorAll("[data-thumb]").forEach((el) => {
        el.classList.toggle("is-active", String(el.dataset.thumb) === String(id));
      });
      root.querySelectorAll("[data-dot]").forEach((el) => {
        el.classList.toggle("is-active", String(el.dataset.dot) === String(id));
      });
      const mediaItems = [...root.querySelectorAll("[data-media-id]")];
      const activeIndex = mediaItems.findIndex((el) => String(el.dataset.mediaId) === String(id));
      const previous = root.querySelector("[data-gallery-prev]");
      const next = root.querySelector("[data-gallery-next]");
      if (previous) previous.disabled = activeIndex <= 0;
      if (next) next.disabled = activeIndex < 0 || activeIndex >= mediaItems.length - 1;
    };

    const target = root.querySelector(`[data-media-id="${id}"]`);
    const image = target?.querySelector("img");
    if (!image || image.complete) {
      activate();
      return;
    }

    const request = String(Number(root.dataset.mediaRequest || 0) + 1);
    root.dataset.mediaRequest = request;
    image.loading = "eager";
    const reveal = () => {
      if (root.dataset.mediaRequest === request) activate();
    };
    image.addEventListener("load", reveal, { once: true });
    image.addEventListener("error", reveal, { once: true });
  };

  const bindProduct = (section) => {
    if (!section || section.dataset.bound === "product") return;
    section.dataset.bound = "product";
    const product = parseProductJson(section);
    const form = section.querySelector("form[action*='/cart/add'], .product-form, [data-custom-form]");
    bindDeliveryEstimate(section);
    bindStickyPurchase(section);
    section.addEventListener("change", (event) => {
      if (event.target.matches("[data-option-input]") && product && form) {
        const variant = findVariant(product, optionValues(form));
        updateProductUI(section, variant);
        event.target.closest(".choice")?.parentElement?.querySelectorAll(".choice").forEach((el) => el.classList.remove("is-active"));
        event.target.closest(".choice")?.classList.add("is-active");
      }
    });
    section.addEventListener("click", (event) => {
      const qty = event.target.closest("[data-qty-btn]");
      if (qty && form) {
        const input = form.querySelector('input[name="quantity"]');
        if (input) input.value = Math.max(1, Number(input.value || 1) + Number(qty.dataset.qtyBtn));
      }
      const thumb = event.target.closest("[data-thumb]");
      if (thumb) showMedia(section, thumb.dataset.thumb);
    });
  };

  const bindCustomizer = (root) => {
    if (!root || root.dataset.bound === "custom-v19") return;
    root.dataset.bound = "custom-v19";

    const canOrder = root.dataset.canOrder === "true";
    const form = root.querySelector("#CustomArtProductForm");
    const product = parseProductJson(root);
    let customVariants = [];
    try { customVariants = JSON.parse(root.querySelector("[data-custom-variants]")?.textContent || "[]"); } catch { customVariants = []; }
    const fileInput = root.querySelector("[data-file]");
    const artworkPreviewInput = root.querySelector("[data-artwork-preview-file]");
    const cropStage = root.querySelector("[data-preview-stage]");
    const cropPanel = root.querySelector("[data-preview-panel]");
    const cropImage = root.querySelector("[data-preview-image]");
    const cropEmpty = root.querySelector("[data-preview-empty]");
    const uploadIdle = root.querySelector("[data-upload-idle]");
    const uploadDone = root.querySelector("[data-upload-done]");
    const uploadError = root.querySelector("[data-upload-error]");
    const variantInput = root.querySelector("[data-variant-id]");
    const fitInput = root.querySelector("[data-fit-input]");
    const zoomInput = root.querySelector("[data-zoom-input]");
    const positionInput = root.querySelector("[data-position-input]");
    const orientationInput = root.querySelector("[data-orientation-input]");
    const note = root.querySelector("[data-note]");
    const womenPrivacy = root.querySelector("[data-women-privacy]");
    const addButton = root.querySelector("[data-add-to-cart]");
    const roomStage = root.querySelector("[data-room-stage]");
    const roomModal = root.querySelector("[data-room-preview-modal]");
    const roomModalSheet = roomModal?.querySelector(".room-preview-sheet");
    const roomBg = root.querySelector("[data-room-bg]");
    const roomSlot = root.querySelector("[data-room-slot]");
    const roomArt = root.querySelector("[data-room-art]");
    const roomEmpty = root.querySelector("[data-room-empty]");
    const compareArtworks = root.querySelector("[data-compare-artworks]");
    const compareItems = [...root.querySelectorAll("[data-compare-item]")];
    const panelCard = root.querySelector(".create-panel-card");
    const progress = root.querySelector(".create-progress");
    const mainPreview = root.querySelector(".create-preview");
    const mobileFloatingPreview = root.querySelector("[data-mobile-floating-preview]");
    const mobilePreviewFrame = root.querySelector("[data-mobile-preview-frame]");
    const mobilePreviewImage = root.querySelector("[data-mobile-preview-image]");
    const stepSections = [...root.querySelectorAll("[data-step]")];
    const sizeButtons = [...root.querySelectorAll("[data-size]")];
    const orientationButtons = [...root.querySelectorAll("[data-orientation]")];
    const sceneButtons = [...root.querySelectorAll("[data-preview-scene]")];
    let objectUrl = "";
    let sourceImage = null;
    let renderedArtworkUrl = "";
    let renderedArtworkKey = "";
    let previewFileKey = "";
    let previewFileTimer = 0;
    let roomLoadToken = 0;
    let roomScenesPreloaded = false;
    const renderCanvas = document.createElement("canvas");
    const customText = cfg.strings || {};

    const normalize = (value) => String(value || "")
      .toLowerCase()
      .replace(/[×✕]/g, "x")
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9x]/g, "");
    const firstSize = sizeButtons.find((button) => button.classList.contains("is-active")) || sizeButtons[0];
    const firstOrientation = orientationButtons.find((button) => button.classList.contains("is-active")) || orientationButtons[0];
    const state = {
      sizeValue: firstSize?.dataset.sizeValue || firstSize?.dataset.size || "20X30 cm",
      sizeLabel: firstSize?.dataset.size || "20 × 30 cm",
      orientation: firstOrientation?.dataset.orientation || "Portrait",
      scene: "office",
      fit: "fill",
      zoom: 100,
      posX: 50,
      posY: 50,
      step: "photo",
    };

    const sceneSettings = {
      office: { label: customText.roomOffice || "Office", reference: 200, furnitureVisual: 82, x: 50, bottom: 42, zoom: 1.34, objectPosition: "50% 56%" },
      bedroom: { label: customText.roomBedroom || "Bedroom", reference: 180, furnitureVisual: 76, x: 50, bottom: 43, zoom: 1.22, objectPosition: "50% 57%" },
      living: { label: customText.roomLiving || "Living Room", reference: 216, furnitureVisual: 78, x: 52, y: 33, zoom: 1.28, objectPosition: "52% 57%" },
      compare: { label: customText.roomComparison || "Size Comparison", reference: 216, furnitureVisual: 67, x: 50, y: 35, zoom: 1, objectPosition: "50% 50%" },
    };

    const hasImage = () => Boolean(objectUrl);
    const isDesktop = () => window.matchMedia("(min-width: 1025px)").matches;
    const setText = (selector, textValue) => root.querySelectorAll(selector).forEach((element) => { element.textContent = textValue; });
    const orientationLabel = () => normalize(state.orientation).includes("landscape")
      ? (customText.orientationLandscape || "Landscape")
      : (customText.orientationPortrait || "Portrait");
    const dimensions = () => {
      const values = String(state.sizeLabel).match(/\d+(?:\.\d+)?/g) || [20, 30];
      const shortSide = Number(values[0]) || 20;
      const longSide = Number(values[1]) || 30;
      return normalize(state.orientation).includes("landscape")
        ? { width: longSide, height: shortSide }
        : { width: shortSide, height: longSide };
    };
    const findVariant = (sizeValue = state.sizeValue, orientation = state.orientation) => {
      const variants = customVariants.length ? customVariants : (product?.variants || []);
      const sizeKey = normalize(sizeValue);
      const sizeCore = sizeKey.replace(/cm$/, "");
      const orientationKey = normalize(orientation);
      const requestedSizeButton = sizeButtons.find((button) => normalize(button.dataset.sizeValue || button.dataset.size) === sizeKey);
      const explicitVariantId = orientationKey.includes("landscape")
        ? requestedSizeButton?.dataset.variantLandscapeId
        : requestedSizeButton?.dataset.variantPortraitId;
      const explicitVariant = explicitVariantId && variants.find((variant) => String(variant.id) === String(explicitVariantId));
      if (explicitVariant) return explicitVariant;
      const sizeNumbers = (String(sizeValue).match(/\d+(?:\.\d+)?/g) || []).slice(0, 2).map(Number).sort((a, b) => a - b);
      const variantValues = (variant) => [...(variant.options || []), variant.option1, variant.option2, variant.title].filter(Boolean);
      const variantMatchesSize = (variant) => {
        const values = variantValues(variant);
        if (values.map(normalize).includes(sizeKey)) return true;
        const numbers = values.join(" ").match(/\d+(?:\.\d+)?/g)?.slice(0, 2).map(Number).sort((a, b) => a - b) || [];
        return sizeNumbers.length === 2 && numbers.length === 2 && numbers[0] === sizeNumbers[0] && numbers[1] === sizeNumbers[1];
      };
      const variantMatchesOrientation = (variant) => {
        const combined = normalize(variantValues(variant).join(" "));
        if (combined.includes(orientationKey)) return true;
        if (orientationKey.includes("landscape")) return combined.includes("horizontal");
        if (orientationKey.includes("portrait")) return combined.includes("vertical");
        return false;
      };
      const matched = variants.find((variant) => {
        const values = variantValues(variant).map(normalize);
        const combined = normalize(variantValues(variant).join(" "));
        return (values.includes(sizeKey) && values.includes(orientationKey))
          || (combined.includes(sizeCore) && combined.includes(orientationKey));
      });
      if (matched) return matched;
      const sameSizeVariants = variants.filter(variantMatchesSize);
      const sameSizeOrientation = sameSizeVariants.find(variantMatchesOrientation);
      if (sameSizeOrientation) return sameSizeOrientation;
      if (sameSizeVariants.length === 1) return sameSizeVariants[0];
      const orientationIndexForSize = normalize(orientationButtons[0]?.dataset.orientation) === orientationKey ? 0 : 1;
      if (sameSizeVariants.length >= 2 && sameSizeVariants[orientationIndexForSize]) return sameSizeVariants[orientationIndexForSize];
      const uniqueSizes = [...new Set(sizeButtons.map((button) => normalize(button.dataset.sizeValue || button.dataset.size)))];
      const uniqueOrientations = [...new Set(orientationButtons.map((button) => normalize(button.dataset.orientation)))];
      const sizeIndex = uniqueSizes.indexOf(sizeKey);
      const orientationIndex = uniqueOrientations.indexOf(orientationKey);
      const fallbackIndex = sizeIndex * uniqueOrientations.length + orientationIndex;
      return sizeIndex >= 0 && orientationIndex >= 0 ? variants[fallbackIndex] : null;
    };
    const showError = (message = "") => {
      if (!uploadError) return;
      uploadError.hidden = !message;
      uploadError.textContent = message;
    };
    const applyCropRatio = () => {
      if (!cropStage || !cropPanel) return;
      const size = dimensions();
      cropStage.style.setProperty("--crop-ratio", `${size.width} / ${size.height}`);
      cropStage.dataset.orientation = normalize(state.orientation).includes("landscape") ? "landscape" : "portrait";
    };
    const renderArtwork = () => {
      if (!hasImage() || !sourceImage?.complete || !sourceImage.naturalWidth) return objectUrl;
      const landscape = normalize(state.orientation).includes("landscape");
      const canvasWidth = landscape ? 900 : 600;
      const canvasHeight = landscape ? 600 : 900;
      const cacheKey = [objectUrl, canvasWidth, canvasHeight, state.fit, state.zoom, state.posX, state.posY].join("|");
      if (cacheKey === renderedArtworkKey && renderedArtworkUrl) return renderedArtworkUrl;
      renderCanvas.width = canvasWidth;
      renderCanvas.height = canvasHeight;
      const context = renderCanvas.getContext("2d");
      if (!context) return objectUrl;
      context.fillStyle = "#f8f5ef";
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      const baseScale = state.fit === "fit"
        ? Math.min(canvasWidth / sourceImage.naturalWidth, canvasHeight / sourceImage.naturalHeight)
        : Math.max(canvasWidth / sourceImage.naturalWidth, canvasHeight / sourceImage.naturalHeight);
      const scale = baseScale * (state.zoom / 100);
      const drawWidth = sourceImage.naturalWidth * scale;
      const drawHeight = sourceImage.naturalHeight * scale;
      const drawX = ((canvasWidth - drawWidth) / 2) + (((state.posX - 50) / 100) * canvasWidth);
      const drawY = ((canvasHeight - drawHeight) / 2) + (((state.posY - 50) / 100) * canvasHeight);
      context.drawImage(sourceImage, drawX, drawY, drawWidth, drawHeight);
      renderedArtworkUrl = renderCanvas.toDataURL("image/jpeg", 0.92);
      renderedArtworkKey = cacheKey;
      return renderedArtworkUrl;
    };
    const syncArtworkPreviewFile = () => {
      if (!artworkPreviewInput || !hasImage() || !renderedArtworkKey || !renderedArtworkUrl.startsWith("data:image/jpeg")) return;
      if (previewFileKey === renderedArtworkKey && artworkPreviewInput.files?.length) return;
      try {
        const encoded = renderedArtworkUrl.split(",")[1] || "";
        const binary = window.atob(encoded);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
        const transfer = new DataTransfer();
        const sizeName = normalize(state.sizeLabel).replace(/cm$/, "");
        transfer.items.add(new File([bytes], `artwork-preview-${sizeName}-${normalize(state.orientation)}.jpg`, { type: "image/jpeg" }));
        artworkPreviewInput.files = transfer.files;
        previewFileKey = renderedArtworkKey;
      } catch {
        previewFileKey = "";
      }
    };
    const queueArtworkPreviewFile = () => {
      window.clearTimeout(previewFileTimer);
      previewFileTimer = window.setTimeout(syncArtworkPreviewFile, 120);
    };
    const applyRoomGeometry = () => {
      if (!roomStage || !roomSlot) return;
      const settings = sceneSettings[state.scene] || sceneSettings.office;
      const size = dimensions();
      const width = (size.width / settings.reference) * settings.furnitureVisual;
      const height = width * (size.height / size.width) * (settings.geometryRatio || 1);
      const top = Number.isFinite(settings.bottom) ? settings.bottom - (height / 2) : settings.y;
      roomStage.dataset.scene = state.scene;
      roomStage.style.setProperty("--scene-zoom", String(settings.zoom));
      roomStage.style.setProperty("--scene-position", settings.objectPosition);
      roomStage.style.setProperty("--room-art-width", `${width}%`);
      roomStage.style.setProperty("--room-art-ratio", `${size.width} / ${size.height}`);
      roomStage.style.setProperty("--room-art-left", `${settings.x}%`);
      roomStage.style.setProperty("--room-art-top", `${top}%`);
    };
    const preloadRoomScenes = () => {
      if (roomScenesPreloaded) return;
      roomScenesPreloaded = true;
      sceneButtons.forEach((button) => {
        const source = button.dataset.sceneSrc;
        if (source) (new Image()).src = source;
      });
    };
    const setRoomBackground = (source) => {
      if (!roomBg || !roomStage || !source) return;
      const token = ++roomLoadToken;
      const currentSource = roomBg.getAttribute("src") || "";
      const reveal = () => {
        if (token !== roomLoadToken) return;
        requestAnimationFrame(() => requestAnimationFrame(() => roomStage.classList.remove("is-loading")));
      };
      const revealAfterDecode = () => {
        if (typeof roomBg.decode === "function") roomBg.decode().then(reveal).catch(reveal);
        else reveal();
      };
      if (currentSource === source && roomBg.complete && roomBg.naturalWidth) {
        revealAfterDecode();
        return;
      }
      roomStage.classList.add("is-loading");
      roomBg.addEventListener("load", revealAfterDecode, { once: true });
      roomBg.addEventListener("error", reveal, { once: true });
      if (currentSource !== source) roomBg.src = source;
      if (roomBg.complete && roomBg.naturalWidth) revealAfterDecode();
    };
    const paintRoom = () => {
      if (!roomStage) return;
      const activeSceneButton = sceneButtons.find((button) => button.dataset.previewScene === state.scene);
      const settings = sceneSettings[state.scene] || sceneSettings.office;
      const backgroundSource = activeSceneButton?.dataset.sceneSrc;
      const artworkSource = renderArtwork();
      setRoomBackground(backgroundSource);
      if (roomBg) roomBg.alt = `${settings.label} artwork preview`;
      roomStage.dataset.fit = state.fit;
      setText("[data-room-name]", settings.label);
      setText("[data-room-note]", state.scene === "compare"
        ? (customText.compareNote || "All four sizes share one clean wall and one camera position for an accurate comparison.")
        : (customText.roomNote || "Artwork placement is preset at the center of the wall for a consistent preview."));
      sceneButtons.forEach((button) => {
        const active = button.dataset.previewScene === state.scene;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-selected", String(active));
      });
      if (roomSlot) roomSlot.hidden = state.scene === "compare";
      if (compareArtworks) compareArtworks.hidden = state.scene !== "compare";
      applyRoomGeometry();
      if (roomArt) {
        roomArt.hidden = !hasImage();
        if (hasImage()) roomArt.src = artworkSource;
        roomArt.style.objectFit = "cover";
        roomArt.style.objectPosition = "50% 50%";
        roomArt.style.transform = "none";
      }
      if (roomEmpty) roomEmpty.hidden = hasImage();
      if (compareItems.length) {
        const comparisonDimensions = compareItems.map((item) => {
          const values = String(item.dataset.compareSize || "20 × 30 cm").match(/\d+(?:\.\d+)?/g) || [20, 30];
          const shortSide = Number(values[0]) || 20;
          const longSide = Number(values[1]) || 30;
          return normalize(state.orientation).includes("landscape")
            ? { width: longSide, height: shortSide }
            : { width: shortSide, height: longSide };
        });
        const portraitWidthTotal = comparisonDimensions.reduce((sum, size) => sum + Math.min(size.width, size.height), 0) || 140;
        compareItems.forEach((item, index) => {
          const size = comparisonDimensions[index];
          item.style.setProperty("--compare-width", `${(size.width / portraitWidthTotal) * 68}%`);
          item.style.setProperty("--compare-ratio", `${size.width} / ${size.height}`);
          const image = item.querySelector("[data-compare-art]");
          if (!image) return;
          image.hidden = !hasImage();
          if (hasImage()) image.src = artworkSource;
          image.style.objectFit = "cover";
          image.style.objectPosition = "50% 50%";
          image.style.transform = "none";
        });
      }
      root.querySelectorAll("[data-guide-size]").forEach((button) => {
        const active = normalize(button.dataset.sizeValue) === normalize(state.sizeValue);
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-checked", String(active));
      });
    };
    const paintCrop = () => {
      applyCropRatio();
      if (cropStage) {
        cropStage.classList.toggle("has-image", hasImage());
        cropStage.dataset.fit = state.fit;
        cropStage.tabIndex = hasImage() ? -1 : 0;
      }
      if (cropImage) {
        cropImage.hidden = !hasImage();
        if (hasImage()) cropImage.src = objectUrl;
        cropImage.style.objectFit = state.fit === "fit" ? "contain" : state.fit === "center" ? "none" : "cover";
        cropImage.style.objectPosition = "50% 50%";
        cropImage.style.transform = `translate(${state.posX - 50}%, ${state.posY - 50}%) scale(${state.zoom / 100})`;
      }
      if (cropEmpty) cropEmpty.hidden = hasImage();
      if (uploadIdle) uploadIdle.hidden = hasImage();
      if (uploadDone) uploadDone.hidden = !hasImage();
      root.querySelectorAll("[data-open-room-preview]").forEach((button) => { button.disabled = !hasImage(); });
      const thumb = root.querySelector("[data-thumb]");
      if (thumb && hasImage()) thumb.src = objectUrl;
      setText("[data-photo-meta]", hasImage() ? (customText.uploaded || "Uploaded") : (customText.notAdded || "Not added"));
      setText("[data-sum-photo]", hasImage() ? (customText.uploaded || "Uploaded") : (customText.notAdded || "Not added"));
      setText("[data-sum-size]", state.sizeLabel);
      setText("[data-sum-orientation]", orientationLabel());
      root.querySelectorAll("[data-fit]").forEach((button) => {
        const active = button.dataset.fit === state.fit;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-checked", String(active));
      });
      const zoomControl = root.querySelector("[data-zoom]");
      if (zoomControl) zoomControl.value = String(state.zoom);
      if (mobileFloatingPreview) {
        mobileFloatingPreview.hidden = !hasImage();
        mobileFloatingPreview.classList.toggle("has-image", hasImage());
      }
      if (mobilePreviewFrame) mobilePreviewFrame.style.setProperty("--mobile-preview-ratio", `${dimensions().width} / ${dimensions().height}`);
      if (mobilePreviewImage) {
        mobilePreviewImage.hidden = !hasImage();
        if (hasImage()) mobilePreviewImage.src = renderArtwork();
      }
      setText("[data-mobile-preview-meta]", `${state.sizeLabel} · ${orientationLabel()}`);
      paintRoom();
      if (hasImage()) queueArtworkPreviewFile();
    };
    const syncVariant = () => {
      const variant = findVariant();
      if (!variant) {
        if (addButton) addButton.disabled = true;
        showError(customText.unavailableCombination || "This size and orientation combination is not available.");
        return false;
      }
      showError();
      if (variantInput) variantInput.value = variant.id;
      root.querySelectorAll("[data-live-price]").forEach((element) => { element.textContent = formatMoney(variant.price); });
      root.querySelectorAll("[data-live-compare]").forEach((element) => {
        const showCompare = variant.compare_at_price > variant.price;
        element.hidden = !showCompare;
        element.textContent = showCompare ? formatMoney(variant.compare_at_price) : "";
      });
      sizeButtons.forEach((button) => {
        const candidate = findVariant(button.dataset.sizeValue || button.dataset.size, state.orientation);
          const candidateUnavailable = !candidate || candidate.available === false;
  button.disabled = candidateUnavailable;
  button.classList.toggle("is-sold-out", candidateUnavailable);
  button.setAttribute("aria-disabled", String(candidateUnavailable));
        const price = button.querySelector(".size-price-current");
        const compare = button.querySelector(".size-price-compare");
        if (price && candidate) price.textContent = formatMoney(candidate.price);
        if (compare && candidate) {
          const showCompare = candidate.compare_at_price > candidate.price;
          compare.hidden = !showCompare;
          compare.textContent = showCompare ? formatMoney(candidate.compare_at_price) : "";
        }
      });
      const unavailable = !canOrder || variant.available === false;
      if (addButton) addButton.disabled = unavailable;
      root.querySelectorAll(".create-sticky [name='add']").forEach((button) => { button.disabled = unavailable; });
      if (canOrder) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState({}, "", url);
      }
      return true;
    };
    const chooseSize = (button) => {
      if (!button) return;
      state.sizeValue = button.dataset.sizeValue || button.dataset.size;
      state.sizeLabel = button.dataset.size || button.dataset.guideSize || state.sizeValue.replace(/x/i, " × ");
      sizeButtons.forEach((item) => {
        const active = normalize(item.dataset.sizeValue || item.dataset.size) === normalize(state.sizeValue);
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-checked", String(active));
      });
      syncVariant();
      paintCrop();
    };
    const chooseOrientation = (button) => {
      if (!button) return;
      state.orientation = button.dataset.orientation || "Portrait";
      if (orientationInput) orientationInput.value = state.orientation;
      orientationButtons.forEach((item) => {
        const active = normalize(item.dataset.orientation) === normalize(state.orientation);
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-checked", String(active));
      });
      syncVariant();
      paintCrop();
    };
    const chooseScene = (button) => {
      if (!button) return;
      state.scene = button.dataset.previewScene || "office";
      paintRoom();
    };
    let manualStepUntil = 0;
    const setActiveStep = (name) => {
      state.step = name;
      root.querySelectorAll("[data-goto]").forEach((button) => {
        const active = button.dataset.goto === name;
        button.classList.toggle("is-active", active);
        button.toggleAttribute("aria-current", active);
      });
    };
    const openStep = (name, shouldScroll = false) => {
      setActiveStep(name);
      stepSections.forEach((section) => section.classList.toggle("is-open", isDesktop() || section.dataset.step === name));
      root.querySelectorAll("[data-toggle-step]").forEach((button) => button.setAttribute("aria-expanded", String(isDesktop() || button.dataset.toggleStep === name)));
      if (!shouldScroll) return;
      manualStepUntil = performance.now() + 800;
      const section = stepSections.find((item) => item.dataset.step === name);
      if (!section) return;
      if (isDesktop() && panelCard?.contains(section)) {
        const offset = Math.max(0, section.offsetTop - (progress?.offsetHeight || 0) - 14);
        panelCard.scrollTo({ top: offset, behavior: "smooth" });
      } else {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    const loadFile = (selectedFile) => {
      if (!selectedFile) return;
      const declaredType = String(selectedFile.type || "").toLowerCase();
      const trustedImageType = ["image/jpeg", "image/png", "image/webp"].includes(declaredType);
      const genericType = !declaredType || declaredType === "application/octet-stream";
      const validType = trustedImageType || (genericType && /\.(jpe?g|png|webp)$/i.test(selectedFile.name));
      if (!validType) {
        showError(customText.uploadInvalidType || "Please upload a JPG, PNG or WEBP image.");
        if (fileInput) fileInput.value = "";
        return;
      }
      if (selectedFile.size > 20 * 1024 * 1024) {
        showError(customText.uploadTooLarge || "That file is too large. Please choose an image up to 20 MB.");
        if (fileInput) fileInput.value = "";
        return;
      }
      showError();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(selectedFile);
      renderedArtworkUrl = "";
      renderedArtworkKey = "";
      previewFileKey = "";
      if (artworkPreviewInput) artworkPreviewInput.value = "";
      const pendingImage = new Image();
      sourceImage = pendingImage;
      pendingImage.onload = () => {
        if (sourceImage !== pendingImage) return;
        if (!pendingImage.naturalWidth || !pendingImage.naturalHeight || pendingImage.naturalWidth * pendingImage.naturalHeight > 100000000) {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          objectUrl = "";
          sourceImage = null;
          if (fileInput) fileInput.value = "";
          showError(customText.uploadInvalidType || "Please upload a valid JPG, PNG or WEBP image.");
          paintCrop();
          return;
        }
        renderedArtworkKey = "";
        paintCrop();
      };
      pendingImage.onerror = () => {
        if (sourceImage !== pendingImage) return;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = "";
        sourceImage = null;
        if (fileInput) fileInput.value = "";
        showError(customText.uploadInvalidType || "Please upload a valid JPG, PNG or WEBP image.");
        paintCrop();
      };
      pendingImage.src = objectUrl;
      preloadRoomScenes();
      state.posX = 50;
      state.posY = 50;
      state.zoom = 100;
      const fileName = root.querySelector("[data-file-name]");
      if (fileName) fileName.textContent = selectedFile.name;
      paintCrop();
    };
    const pickFile = () => fileInput?.click();
    const openRoomPreview = () => {
      if (!hasImage() || !roomModal) {
        showError(customText.roomNeedPhoto || "Please upload your photo before opening the room preview.");
        openStep("photo", true);
        return;
      }
      showError();
      preloadRoomScenes();
      roomStage?.classList.add("is-loading");
      paintRoom();
      roomModal.hidden = false;
      document.body.classList.add("room-preview-open");
      requestAnimationFrame(() => roomModalSheet?.focus());
    };
    const closeRoomPreview = () => {
      if (!roomModal) return;
      roomModal.hidden = true;
      document.body.classList.remove("room-preview-open");
      root.querySelector("[data-open-room-preview]")?.focus();
    };

    if (form) form.setAttribute("enctype", "multipart/form-data");
    if (isDesktop()) root.querySelectorAll("[data-step]").forEach((section) => section.classList.add("is-open"));
    else openStep("photo");
    syncVariant();
    paintCrop();

    if (mainPreview && mobileFloatingPreview && "IntersectionObserver" in window) {
      const previewObserver = new IntersectionObserver(([entry]) => {
        mobileFloatingPreview.classList.toggle("is-visible", !entry.isIntersecting);
      }, { threshold: 0.08 });
      previewObserver.observe(mainPreview);
    }

    cropStage?.addEventListener("click", (event) => { if (!hasImage() && !event.target.closest("button")) pickFile(); });
    cropStage?.addEventListener("keydown", (event) => {
      if (!hasImage() && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); pickFile(); }
    });
    cropStage?.addEventListener("dragover", (event) => { event.preventDefault(); cropStage.classList.add("is-over"); });
    cropStage?.addEventListener("dragleave", () => cropStage.classList.remove("is-over"));
    cropStage?.addEventListener("drop", (event) => {
      event.preventDefault();
      cropStage.classList.remove("is-over");
      const dropped = event.dataTransfer.files?.[0];
      if (dropped && fileInput && typeof DataTransfer !== "undefined") {
        const transfer = new DataTransfer();
        transfer.items.add(dropped);
        fileInput.files = transfer.files;
      }
      loadFile(dropped);
    });
    fileInput?.addEventListener("change", () => loadFile(fileInput.files?.[0]));
    window.addEventListener("pagehide", () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, { once: true });

    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-open-room-preview]")) { event.preventDefault(); openRoomPreview(); return; }
      if (event.target.closest("[data-close-room-preview]")) { event.preventDefault(); closeRoomPreview(); return; }
      if (event.target.closest("[data-replace-image], [data-upload-idle]")) { event.preventDefault(); pickFile(); return; }
      if (event.target.closest("[data-remove-image]")) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = "";
        sourceImage = null;
        renderedArtworkUrl = "";
        renderedArtworkKey = "";
        previewFileKey = "";
        if (fileInput) fileInput.value = "";
        if (artworkPreviewInput) artworkPreviewInput.value = "";
        paintCrop();
        return;
      }
      const size = event.target.closest("[data-size]");
      if (size) { chooseSize(size); return; }
      const guide = event.target.closest("[data-guide-size]");
      if (guide) {
        chooseSize(sizeButtons.find((button) => normalize(button.dataset.sizeValue || button.dataset.size) === normalize(guide.dataset.sizeValue)));
        return;
      }
      const orientation = event.target.closest("[data-orientation]");
      if (orientation) { chooseOrientation(orientation); return; }
      const scene = event.target.closest("[data-preview-scene]");
      if (scene) {
        chooseScene(scene);
        if (scene.dataset.goto) openStep(scene.dataset.goto, true);
        return;
      }
      const fit = event.target.closest("[data-fit]");
      if (fit) {
        state.fit = fit.dataset.fit;
        if (fitInput) fitInput.value = state.fit;
        if (state.fit !== "fill") { state.posX = 50; state.posY = 50; }
        paintCrop();
        return;
      }
      const goto = event.target.closest("[data-goto]");
      if (goto) { openStep(goto.dataset.goto, true); return; }
      const toggle = event.target.closest("[data-toggle-step]");
      if (toggle && !isDesktop()) openStep(toggle.dataset.toggleStep);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && roomModal && !roomModal.hidden) closeRoomPreview();
    });

    root.querySelector("[data-zoom]")?.addEventListener("input", (event) => {
      state.zoom = Number(event.target.value);
      const limit = Math.max(0, (state.zoom - 100) / 2);
      state.posX = Math.min(50 + limit, Math.max(50 - limit, state.posX));
      state.posY = Math.min(50 + limit, Math.max(50 - limit, state.posY));
      paintCrop();
    });
    note?.addEventListener("input", () => {
      const count = root.querySelector("[data-note-count]");
      if (count) count.textContent = `${note.value.length} / 150`;
      setText("[data-sum-note-state]", note.value ? (customText.included || "Included") : (customText.none || "None"));
    });
    womenPrivacy?.addEventListener("change", () => {
      setText("[data-sum-women-privacy]", womenPrivacy.checked ? (customText.requested || "Requested") : (customText.notRequested || "Not requested"));
    });

    if (cropImage && cropStage) {
      let dragging = false;
      let origin = { x: 0, y: 0, px: 50, py: 50 };
      cropImage.addEventListener("pointerdown", (event) => {
        if (state.fit !== "fill" || !hasImage()) return;
        dragging = true;
        cropStage.classList.add("is-dragging");
        origin = { x: event.clientX, y: event.clientY, px: state.posX, py: state.posY };
        cropImage.setPointerCapture(event.pointerId);
      });
      cropImage.addEventListener("pointermove", (event) => {
        if (!dragging) return;
        const rect = cropImage.getBoundingClientRect();
        if (state.zoom < 110) state.zoom = 110;
        const limit = Math.max(0, (state.zoom - 100) / 2);
        state.posX = Math.min(50 + limit, Math.max(50 - limit, origin.px + ((event.clientX - origin.x) / rect.width) * 100));
        state.posY = Math.min(50 + limit, Math.max(50 - limit, origin.py + ((event.clientY - origin.y) / rect.height) * 100));
        paintCrop();
      });
      cropImage.addEventListener("pointerup", () => { dragging = false; cropStage.classList.remove("is-dragging"); });
      cropImage.addEventListener("pointercancel", () => { dragging = false; cropStage.classList.remove("is-dragging"); });
    }

    if (panelCard && progress && isDesktop()) {
      let scrollFrame = 0;
      panelCard.addEventListener("scroll", () => {
        if (scrollFrame) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;
          if (performance.now() < manualStepUntil) return;
          const marker = panelCard.getBoundingClientRect().top + progress.offsetHeight + 34;
          const panelSteps = stepSections.filter((section) => panelCard.contains(section));
          let active = panelSteps[0]?.dataset.step || "photo";
          panelSteps.forEach((section) => {
            if (section.getBoundingClientRect().top <= marker) active = section.dataset.step;
          });
          setActiveStep(active);
        });
      }, { passive: true });
    }

    const cta = root.querySelector("[data-main-cta]");
    const sticky = root.querySelector("[data-sticky-cta]");
    if (cta && sticky) {
      let stickyFrame = 0;
      const syncSticky = () => {
        const mobile = window.matchMedia("(max-width: 749px)").matches;
        const passedButton = cta.getBoundingClientRect().bottom < 0;
        sticky.classList.toggle("is-visible", mobile && passedButton);
      };
      const scheduleSticky = () => {
        if (stickyFrame) return;
        stickyFrame = requestAnimationFrame(() => {
          stickyFrame = 0;
          syncSticky();
        });
      };
      window.addEventListener("scroll", scheduleSticky, { passive: true });
      window.addEventListener("resize", scheduleSticky, { passive: true });
      if ("IntersectionObserver" in window) new IntersectionObserver(syncSticky, { threshold: [0, 0.15] }).observe(cta);
      syncSticky();
    }
    form?.addEventListener("submit", () => {
      if (fitInput) fitInput.value = state.fit;
      if (zoomInput) zoomInput.value = `${state.zoom}%`;
      if (positionInput) positionInput.value = `${Math.round(state.posX)}% horizontal, ${Math.round(state.posY)}% vertical`;
      if (orientationInput) orientationInput.value = state.orientation;
    });
  };

  const bindProductLightbox = (gallery) => {
    const dialog = gallery.parentElement?.querySelector("[data-product-lightbox]");
    const openers = [...gallery.querySelectorAll("[data-gallery-open]")];
    if (!dialog || !openers.length || dialog.dataset.bound === "lightbox") return;
    dialog.dataset.bound = "lightbox";
    const image = dialog.querySelector("[data-lightbox-image]");
    const stage = dialog.querySelector("[data-lightbox-stage]");
    const count = dialog.querySelector("[data-lightbox-count]");
    const zoomValue = dialog.querySelector("[data-lightbox-zoom-value]");
    const previous = dialog.querySelector("[data-lightbox-prev]");
    const next = dialog.querySelector("[data-lightbox-next]");
    let index = 0;
    let scale = 1;
    let panX = 0;
    let panY = 0;
    let opener = null;
    let gestureStartX = 0;
    let gestureHadPinch = false;
    let pinchDistance = 0;
    let pinchScale = 1;
    const pointers = new Map();
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const applyTransform = () => {
      const maxX = stage.clientWidth * (scale - 1) / 2;
      const maxY = stage.clientHeight * (scale - 1) / 2;
      panX = clamp(panX, -maxX, maxX);
      panY = clamp(panY, -maxY, maxY);
      image.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
      zoomValue.textContent = `${Math.round(scale * 100)}%`;
      stage.classList.toggle("is-zoomed", scale > 1);
      dialog.querySelector("[data-lightbox-zoom-out]").disabled = scale <= 1;
      dialog.querySelector("[data-lightbox-zoom-in]").disabled = scale >= 3;
    };
    const setScale = (value) => {
      scale = clamp(value, 1, 3);
      if (scale === 1) panX = panY = 0;
      applyTransform();
    };
    const showImage = (nextIndex) => {
      index = clamp(nextIndex, 0, openers.length - 1);
      const source = openers[index];
      image.src = source.dataset.galleryFull;
      image.alt = source.dataset.galleryAlt || "";
      count.textContent = `${index + 1} / ${openers.length}`;
      previous.disabled = index === 0;
      next.disabled = index === openers.length - 1;
      setScale(1);
    };
    gallery.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-gallery-open]");
      if (!trigger || !gallery.contains(trigger)) return;
      opener = trigger;
      showImage(openers.indexOf(trigger));
      dialog.showModal();
      dialog.querySelector("[data-lightbox-close]").focus();
    });
    dialog.querySelector("[data-lightbox-close]").addEventListener("click", () => dialog.close());
    dialog.querySelector("[data-lightbox-zoom-in]").addEventListener("click", () => setScale(scale + 0.5));
    dialog.querySelector("[data-lightbox-zoom-out]").addEventListener("click", () => setScale(scale - 0.5));
    previous.addEventListener("click", () => showImage(index - 1));
    next.addEventListener("click", () => showImage(index + 1));
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        const direction = event.key === "ArrowRight" ? 1 : -1;
        showImage(index + (document.documentElement.dir === "rtl" ? -direction : direction));
      }
    });
    dialog.addEventListener("close", () => {
      image.removeAttribute("src");
      pointers.clear();
      opener?.focus();
    });
    stage.addEventListener("wheel", (event) => {
      event.preventDefault();
      setScale(scale + (event.deltaY < 0 ? 0.25 : -0.25));
    }, { passive: false });
    stage.addEventListener("dblclick", () => setScale(scale > 1 ? 1 : 2));
    stage.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      stage.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 1) {
        gestureStartX = event.clientX;
        gestureHadPinch = false;
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
        pinchScale = scale;
        gestureHadPinch = true;
      }
    });
    stage.addEventListener("pointermove", (event) => {
      const old = pointers.get(event.pointerId);
      if (!old) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2 && pinchDistance) {
        const [a, b] = [...pointers.values()];
        setScale(pinchScale * Math.hypot(a.x - b.x, a.y - b.y) / pinchDistance);
      } else if (scale > 1) {
        panX += event.clientX - old.x;
        panY += event.clientY - old.y;
        applyTransform();
      }
    });
    const endPointer = (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      if (!pointers.size && !gestureHadPinch && scale === 1 && Math.abs(event.clientX - gestureStartX) > 50) {
        showImage(index + (event.clientX < gestureStartX ? 1 : -1));
      }
    };
    stage.addEventListener("pointerup", endPointer);
    stage.addEventListener("pointercancel", endPointer);
  };

  const bindGallery = (root) => {
    const gallery = root?.querySelector?.("[data-product-gallery]") || (root?.matches?.("[data-product-gallery]") ? root : null);
    if (!gallery || gallery.dataset.bound === "gallery") return;
    gallery.dataset.bound = "gallery";
    bindProductLightbox(gallery);
    const medias = [...gallery.querySelectorAll("[data-media-id]")];
    const syncStickyOffset = () => {
      if (!window.matchMedia("(min-width: 750px)").matches) {
        gallery.style.removeProperty("--pdp-gallery-sticky-top");
        return;
      }
      const clearance = 16;
      const offset = Math.min(clearance, window.innerHeight - gallery.offsetHeight - clearance);
      gallery.style.setProperty("--pdp-gallery-sticky-top", `${Math.round(offset)}px`);
    };
    requestAnimationFrame(syncStickyOffset);
    if ("ResizeObserver" in window) new ResizeObserver(syncStickyOffset).observe(gallery);
    window.addEventListener("resize", syncStickyOffset, { passive: true });
    if (medias.length < 2) return;
    const move = (direction) => {
      const current = medias.findIndex((media) => !media.hidden);
      const next = Math.min(medias.length - 1, Math.max(0, current + direction));
      if (next !== current) showMedia(gallery, medias[next].dataset.mediaId);
    };
    gallery.querySelector("[data-gallery-prev]")?.addEventListener("click", () => move(-1));
    gallery.querySelector("[data-gallery-next]")?.addEventListener("click", () => move(1));
    let startX = 0;
    gallery.addEventListener("touchstart", (e) => { startX = e.changedTouches[0].clientX; }, { passive: true });
    gallery.addEventListener("touchend", (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      move(dx < 0 ? 1 : -1);
    }, { passive: true });
  };

  const bindCompare = (slider) => {
    if (!slider || slider.dataset.bound) return;
    slider.dataset.bound = "1";
    const handle = slider.querySelector("[data-reveal-handle]");
    const after = slider.querySelector(".reveal-after");
    if (!handle || !after) return;
    const set = (pct) => {
      const v = Math.min(100, Math.max(0, pct));
      after.style.clipPath = `inset(0 0 0 ${v}%)`;
      handle.style.left = `${v}%`;
      handle.setAttribute("aria-valuenow", String(Math.round(v)));
    };
    set(50);
    const move = (clientX) => {
      const rect = slider.getBoundingClientRect();
      set(((clientX - rect.left) / rect.width) * 100);
    };
    handle.addEventListener("pointerdown", (e) => {
      handle.setPointerCapture(e.pointerId);
    });
    handle.addEventListener("pointermove", (e) => {
      if (handle.hasPointerCapture(e.pointerId)) move(e.clientX);
    });
    slider.addEventListener("click", (e) => {
      if (e.target === handle) return;
      move(e.clientX);
    });
    handle.addEventListener("keydown", (e) => {
      const now = Number(handle.getAttribute("aria-valuenow") || 50);
      if (e.key === "ArrowLeft") set(now - 4);
      if (e.key === "ArrowRight") set(now + 4);
    });
  };

  const reveals = () => {
    const nodes = $$(".reveal, .reveal-image");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      nodes.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    nodes.forEach((el) => observer.observe(el));
  };

  const predictive = (() => {
    let timer = 0;
    const run = async (q, box) => {
      if (!box) return;
      if (!q || q.length < 2) {
        box.innerHTML = "";
        return;
      }
      try {
        const res = await fetch(`${predictUrl}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        if (!products.length) {
          box.innerHTML = `<p>${escapeHtml(cfg.strings?.searchNoQuick || "No quick matches.")}</p>`;
          return;
        }
        box.innerHTML = products
          .map((p) => {
            const img = typeof p.featured_image === "string" ? p.featured_image : p.featured_image?.url;
            const raw = p.price;
            const cents = typeof raw === "number" ? raw : Math.round(parseFloat(raw) * 100);
            const price = Number.isFinite(cents) ? formatMoney(cents) : "";
            const type = p.product_type || p.type || "";
            return `<a href="${escapeHtml(safeUrl(p.url))}">${img ? `<img src="${escapeHtml(safeUrl(img, ""))}" alt="" width="48" height="60">` : ""}<span>${escapeHtml(p.title)}${price ? `<small>${price}${type ? ` · ${escapeHtml(type)}` : ""}</small>` : ""}</span></a>`;
          })
          .join("");
      } catch {
        box.innerHTML = "";
      }
    };
    return (input) => {
      const box = input.closest("[data-drawer], form, .search-panel")?.querySelector("[data-search-suggest]") || $("[data-search-suggest]");
      window.clearTimeout(timer);
      timer = window.setTimeout(() => run(input.value.trim(), box), 220);
    };
  })();

  const renderWishlistPage = async () => {
    const grid = $("[data-wishlist-grid]");
    const empty = $("[data-wishlist-empty]");
    const saved = $("[data-wishlist-saved]");
    const count = $("[data-wishlist-count]");
    if (!grid) return;
    const handles = wishRead();
    if (count) count.textContent = String(handles.length);
    if (!handles.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      if (saved) saved.hidden = true;
      return;
    }
    if (empty) empty.hidden = true;
    if (saved) saved.hidden = false;
    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!res.ok) return "";
          const p = await res.json();
          const img = p.featured_image || (p.images && p.images[0]) || "";
          return `<article class="product-card wishlist-card reveal is-in">
            <div class="product-media"><a href="${escapeHtml(safeUrl(p.url))}"><img src="${escapeHtml(safeUrl(img, ""))}" alt="${escapeHtml(p.title)}" width="800" height="1000" loading="lazy"></a>
              <button class="wishlist is-active" type="button" data-wish="${escapeHtml(p.handle)}" aria-pressed="true" aria-label="${escapeHtml(cfg.strings?.cartRemove || "Remove")} ${escapeHtml(p.title)}"><svg class="icon icon-heart" aria-hidden="true"><use href="#icon-heart"></use></svg></button>
            </div>
            <h3><a href="${escapeHtml(safeUrl(p.url))}">${escapeHtml(p.title)}</a></h3>
            <div class="product-meta"><p class="price">${formatMoney(p.price)}</p></div>
          </article>`;
        } catch {
          return "";
        }
      })
    );
    grid.innerHTML = cards.join("");
    syncWish();
  };

  const loadRecommendations = () => {
    $$("product-recommendations[data-url]").forEach(async (el) => {
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const next = doc.querySelector(".shopify-section") || doc.body;
        if (next && next.querySelector(".product-card")) el.replaceWith(next);
      } catch {}
    });
  };

  const onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches("[data-product-form], .product-form, [data-custom-form], form[action*='/cart/add']")) {
      if (!cfg.cartDrawer) return;
      event.preventDefault();
      const err = form.querySelector("[data-product-error]");
      if (err) err.hidden = true;
      if (form.dataset.previewOnly === "true") {
        event.preventDefault();
        return;
      }
      const file = form.querySelector('input[type="file"]');
      if (file && file.required && !file.files?.length) {
        toast(cfg.strings?.needPhoto || "Please upload a photo before adding to bag.");
        return;
      }
      if (form.dataset.adding === "true") return;
      const payload = new FormData(form);
      const submitButtons = $$('button[type="submit"]', form).concat($$(`button[type="submit"][form="${form.id}"]`));
      const buttonStates = submitButtons.map((button) => ({ button, disabled: button.disabled, html: button.innerHTML }));
      form.dataset.adding = "true";
      submitButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        button.textContent = cfg.strings?.uploadingButton || "Uploading…";
      });
      renderCartUploadLoading(form);
      openDrawer("cart");
      try {
        await addToCart(payload);
        toast(cfg.strings?.added || "Added to bag");
        await refreshCart();
      } catch (e) {
        const msg = e?.description || e?.message || cfg.strings?.addError || "Could not update bag.";
        await refreshCart();
        if (err) {
          err.hidden = false;
          err.textContent = msg;
        } else toast(msg);
      } finally {
        delete form.dataset.adding;
        buttonStates.forEach(({ button, disabled, html }) => {
          button.disabled = disabled;
          button.removeAttribute("aria-busy");
          button.innerHTML = html;
        });
      }
    }
  };

  const bindGlobal = () => {
    if (document.body.dataset.emesaBound) return;
    document.body.dataset.emesaBound = "true";

    const header = $(".site-header");
    const onScroll = () => header?.classList.toggle("is-scrolled", window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const positionMegaMenu = (item) => {
      const mega = item?.querySelector(".mega");
      if (!mega || window.matchMedia("(max-width: 989px)").matches) return;
      mega.style.setProperty("--mega-offset-x", "0px");
      const rect = mega.getBoundingClientRect();
      const edge = 20;
      let shift = 0;
      if (rect.right > window.innerWidth - edge) shift -= rect.right - (window.innerWidth - edge);
      if (rect.left + shift < edge) shift += edge - (rect.left + shift);
      mega.style.setProperty("--mega-offset-x", `${Math.round(shift)}px`);
    };
    const positionAllMegaMenus = () => $$(".nav-desktop .nav-item").forEach(positionMegaMenu);
    $$(".nav-desktop .nav-item").forEach((item) => {
      item.addEventListener("mouseenter", () => positionMegaMenu(item));
      item.addEventListener("focusin", () => positionMegaMenu(item));
    });
    window.addEventListener("resize", positionAllMegaMenus, { passive: true });
    positionAllMegaMenus();

    document.addEventListener("click", async (event) => {
      const opener = event.target.closest("[data-open]");
      if (opener) {
        event.preventDefault();
        if (opener.dataset.open === "cart") await refreshCart();
        openDrawer(opener.dataset.open);
        return;
      }
      if (event.target.closest("[data-overlay], button[data-close], .text-link[data-close]")) {
        event.preventDefault();
        closeAll();
        return;
      }
      if (event.target.closest("a[data-close]")) closeAll();

      const productCard = event.target.closest(".product-card[data-product-url]");
      if (productCard && !event.target.closest("a, button, input, select, textarea, label")) {
        window.location.assign(productCard.dataset.productUrl);
        return;
      }

      const acc = event.target.closest("[data-acc]");
      if (acc) {
        const parent = acc.parentElement;
        const open = !parent?.classList.contains("is-open");
        parent?.classList.toggle("is-open", open);
        acc.setAttribute("aria-expanded", String(open));
      }

      const wishBtn = event.target.closest("[data-wish]");
      if (wishBtn) {
        event.preventDefault();
        const on = wishToggle(wishBtn.dataset.wish);
        toast(on ? cfg.strings?.wishOn : cfg.strings?.wishOff);
        syncWish();
        renderWishlistPage();
      }

      const shareBtn = event.target.closest("[data-share-url]");
      if (shareBtn) {
        event.preventDefault();
        const url = new URL(shareBtn.dataset.shareUrl || window.location.href, window.location.origin).href;
        const title = shareBtn.dataset.shareTitle || document.title;
        try {
          if (navigator.share) {
            await navigator.share({ title, url });
          } else {
            await navigator.clipboard.writeText(url);
            toast(cfg.strings?.shareCopied || "Link copied");
          }
        } catch (error) {
          if (error?.name !== "AbortError") toast(cfg.strings?.shareError || "Could not share this item");
        }
        return;
      }

      const quick = event.target.closest("[data-quick-add]");
      if (quick) {
        event.preventDefault();
        if (quick.dataset.needsOptions === "true") {
          window.location.href = quick.closest("article")?.querySelector("a")?.href || window.location.href;
          return;
        }
        try {
          await addToCart({ items: [{ id: Number(quick.dataset.variantId), quantity: 1 }] });
          toast(cfg.strings?.added || "Added to bag");
          await refreshCart();
          openDrawer("cart");
        } catch {
          toast(cfg.strings?.addError);
        }
      }

      const line = event.target.closest(".cart-line");
      if (line?.dataset.key) {
        const key = line.dataset.key;
        const qtyEl = line.querySelector(".qty span");
        const current = Number(qtyEl?.textContent || 1);
        if (event.target.closest("[data-remove]")) {
          await changeLine(key, 0);
          await refreshCart();
          toast(cfg.strings?.removed || "Removed from bag");
        }
        const qtyBtn = event.target.closest("[data-qty]");
        if (qtyBtn) {
          await changeLine(key, Math.max(0, current + Number(qtyBtn.dataset.qty)));
          await refreshCart();
        }
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeAll();
    });
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-cart-quantity]");
      if (!button) return;
      const control = button.closest(".qty-page");
      const input = control?.querySelector('input[type="number"]');
      if (!input) return;
      const next = Math.max(0, Number(input.value || 0) + Number(button.dataset.cartQuantity || 0));
      input.value = next;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-business-quantity]");
      if (!button) return;
      const control = button.closest(".business-quantity");
      const input = control?.querySelector('input[type="number"]');
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.businessQuantity || 0));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    document.addEventListener("submit", onSubmit);
    document.addEventListener("input", (e) => {
      if (e.target.matches("[data-live-search]")) predictive(e.target);
    });
  };

  const bindHeroSlider = (slider) => {
    if (!slider || slider.dataset.bound === "hero-slider") return;
    slider.dataset.bound = "hero-slider";
    const slides = $$('[data-hero-slide]', slider);
    if (slides.length < 2) return;
    const dots = $$('[data-hero-dot]', slider);
    let active = 0;
    let timer;
    const show = (next) => {
      active = (next + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const on = index === active;
        slide.classList.toggle("is-active", on);
        slide.setAttribute("aria-hidden", String(!on));
        slide.querySelectorAll("video").forEach((video) => on ? video.play().catch(() => {}) : video.pause());
      });
      dots.forEach((dot, index) => {
        const on = index === active;
        dot.classList.toggle("is-active", on);
        dot.setAttribute("aria-current", String(on));
      });
    };
    const restart = () => {
      window.clearInterval(timer);
      if (slider.dataset.autoplay === "true" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) timer = window.setInterval(() => show(active + 1), Number(slider.dataset.interval || 6000));
    };
    slider.querySelector("[data-hero-prev]")?.addEventListener("click", () => { show(active - 1); restart(); });
    slider.querySelector("[data-hero-next]")?.addEventListener("click", () => { show(active + 1); restart(); });
    dots.forEach((dot, index) => dot.addEventListener("click", () => { show(index); restart(); }));
    restart();
  };

  const bindFloatingSocial = () => {
    const widget = $("[data-floating-social]");
    if (!widget || widget.dataset.bound === "floating-social") return;
    widget.dataset.bound = "floating-social";
    const toggle = $("[data-floating-social-toggle]", widget);
    if (!toggle) return;
    const close = () => {
      widget.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
    };
    toggle.addEventListener("click", (event) => {
      event.stopPropagation();
      const open = widget.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(open));
    });
    document.addEventListener("click", (event) => {
      if (!widget.contains(event.target)) close();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") close();
    });
  };

  const bindMobileCollectionFilters = () => {
    $$('[data-mobile-filter-panel]').forEach((panel) => {
      if (panel.dataset.bound === 'mobile-filter-panel') return;
      panel.dataset.bound = 'mobile-filter-panel';
      if (panel.dataset.collapseMobile === 'true' && window.matchMedia('(max-width: 989px)').matches) {
        panel.removeAttribute('open');
      }
      const mobileQuery = window.matchMedia('(max-width: 989px)');
      mobileQuery.addEventListener?.('change', (event) => {
        if (event.matches && panel.dataset.collapseMobile === 'true') panel.removeAttribute('open');
        else panel.setAttribute('open', '');
      });
    });
  };

  const init = () => {
    closeAll();
    bindGlobal();
    $$("[data-product-section], [data-customizer]").forEach(bindProduct);
    $$("[data-customizer]").forEach(bindCustomizer);
    $$("[data-reveal-slider]").forEach(bindCompare);
    $$("[data-hero-slider]").forEach(bindHeroSlider);
    $$("[data-product-gallery]").forEach(bindGallery);
    syncWish();
    reveals();
    refreshCart();
    renderWishlistPage();
    loadRecommendations();
    bindFloatingSocial();
    bindMobileCollectionFilters();
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();

  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target;
    bindProduct(root.querySelector("[data-product-section]") || root);
    bindCustomizer(root.querySelector("[data-customizer]") || (root.matches("[data-customizer]") ? root : null));
    $$("[data-reveal-slider]", root).forEach(bindCompare);
    bindGallery(root);
    bindMobileCollectionFilters();
    $$("[data-hero-slider]", root).forEach(bindHeroSlider);
    reveals();
    syncWish();
  });
  document.addEventListener("shopify:section:unload", () => {});
})();
